import * as ReactPlotly from "react-plotly.js";
import type { PlotParams } from "react-plotly.js";
import type { Config, Layout } from "plotly.js";
import de from "plotly.js-locales/de";
import fr from "plotly.js-locales/fr";
import pl from "plotly.js-locales/pl";
import es from "plotly.js-locales/es";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { useTheme } from "@mui/material";

// Vite's CommonJS interop can double-wrap the default export.
interface DefaultWrappedModule {
  default?: unknown;
}
const reactPlotlyModule = ReactPlotly as unknown as DefaultWrappedModule;
const reactPlotlyDefault = reactPlotlyModule.default as
  DefaultWrappedModule | undefined;
const Plot = (reactPlotlyDefault?.default ??
  reactPlotlyModule.default ??
  ReactPlotly) as React.ComponentType<PlotParams>;

const locales = {
  de: de,
  fr: fr,
  pl: pl,
  es: es,
};

export type LocalisedPlotProps = Omit<PlotParams, "config" | "layout"> & {
  layout: Partial<Layout>;
  config?: Omit<Partial<Config>, "locale" | "locales">;
};

export default function LocalisedPlot({
  config,
  layout,
  ...plotProps
}: LocalisedPlotProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const themedLayout = useMemo<Partial<Layout>>(
    () => ({
      colorway: Object.values(theme.palette.data),
      paper_bgcolor: theme.palette.background.paper,
      plot_bgcolor: theme.palette.background.paper,
      ...layout,
      font: { family: theme.typography.fontFamily, ...layout.font },
    }),
    [layout, theme]
  );

  const localisedConfig = useMemo(
    () => ({
      displaylogo: false,
      showSendToCloud: false,
      responsive: true,
      ...config,
      locale: i18n.language,
      locales: locales,
    }),
    [config, i18n.language]
  );

  return <Plot {...plotProps} layout={themedLayout} config={localisedConfig} />;
}
